import * as React from "react";
import { Link, Route, Routes } from "react-router-dom";
import { Badge, Banner, Card, CountPill, EmptyState, FilterPill, Icon, ListStates, PageContainer, PageHeader, SearchInput, Toolbar } from "../components";
import { useAsync } from "../general/useAsync";
import * as Store from "./MarkGridStore";
import { moduleRoute } from "./Shared";
import ModuleOverview from "./ModuleOverview";
import DivisionGrid from "./DivisionGrid";

/**
 * Mark entry starts from the subjects a teacher actually teaches - found through the teaching groups they are enrolled
 * in as an educator - and drills down from there: subject tiles (each subject once, opening on its current term), then
 * division tiles with their weights and the subject's analytics, then a spreadsheet of a division's assessments. The
 * subject page steps between terms and carries the term's subject comments.
 *
 * There is no "class first" path on purpose. A teaching group belongs to one module, and tertiary institutions have
 * no homeroom class at all, so the class is an optional filter carried down the drill-down instead.
 */
export const MarksScreen: React.FC = () => (
    <Routes>
        <Route index element={<MarksHome />} />
        <Route path=":moduleID" element={<ModuleOverview />} />
        <Route path=":moduleID/sheet" element={<DivisionGrid />} />
        <Route path=":moduleID/g/:groupID" element={<ModuleOverview />} />
        <Route path=":moduleID/g/:groupID/sheet" element={<DivisionGrid />} />
    </Routes>
);

const MarksHome: React.FC = () => {
    const [all, setAll] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const subjects = useAsync(() => Store.listSubjects(all), [all]);

    const shown = React.useMemo(() => {
        const term = search.trim().toLowerCase();
        const list = subjects.data?.subjects ?? [];
        if (!term) return list;
        return list.filter((s) =>
            [s.moduleName, s.levelName, ...(s.terms ?? []).flatMap((t) => (t.myTeachingGroups ?? []).map((g) => g.name))]
                .filter(Boolean)
                .some((x) => x!.toLowerCase().includes(term))
        );
    }, [subjects.data, search]);

    // One tile per subject, grouped by grade. The term is chosen on the subject page, not here.
    const sections = React.useMemo(() => {
        const map = new Map<string, Store.MarkGridSubjectModel[]>();
        shown.forEach((s) => {
            const key = s.levelName || "Subjects";
            map.set(key, [...(map.get(key) ?? []), s]);
        });
        return Array.from(map.entries());
    }, [shown]);

    const fellBack = !all && subjects.data && !subjects.data.scoped;

    return (
        <PageContainer>
            <PageHeader
                title="Mark entry"
                icon={["fad", "table-list"]}
                description="Pick a subject to see its divisions, analytics and mark sheet."
            />

            <Toolbar>
                <SearchInput value={search} onChange={setSearch} placeholder="Search subjects or classes..." />
                <FilterPill label="My subjects" active={!all} onToggle={() => setAll(false)} icon={["fad", "user"]} />
                <FilterPill label="All subjects" active={all} onToggle={() => setAll(true)} icon={["fad", "layer-group"]} />
            </Toolbar>

            {fellBack && (
                <Banner tone="info" className="mb-4">
                    You are not linked to any class as an educator yet, so every subject is shown. Once teaching-group
                    enrollments are synced, this list narrows to the subjects you teach.
                </Banner>
            )}

            <ListStates
                isLoading={subjects.isLoading}
                error={subjects.error}
                onRetry={subjects.refresh}
                isEmpty={shown.length === 0}
                empty={{
                    icon: ["fad", "graduation-cap"],
                    title: search ? "No subjects match" : "No subjects yet",
                    hint: search ? "Try a different search." : "Qualification modules appear here once they are set up.",
                }}
            >
                <div className="space-y-6">
                    {sections.map(([title, list]) => (
                        <section key={title}>
                            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-text-weak">
                                {title}
                                <CountPill count={list.length} />
                            </h2>
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-3">
                                {list.map((s) => (
                                    <SubjectTile key={`${s.moduleID}|${s.qualificationProgramLevelID}`} subject={s} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </ListStates>
        </PageContainer>
    );
};

/** A subject opens on its current term; the subject page has the previous / next term controls. */
const SubjectTile: React.FC<{ subject: Store.MarkGridSubjectModel }> = ({ subject: s }) => {
    const terms = s.terms ?? [];
    const current = terms.find((t) => t.qualificationModuleID === s.currentQualificationModuleID) ?? terms[0];
    const mine = current?.myTeachingGroups ?? [];
    // A teacher of exactly one class in the current term lands on that class; anyone else starts on the whole subject.
    const query = mine.length === 1 ? `?class=${encodeURIComponent(mine[0].id ?? "")}` : "";

    return (
        <Link
            to={moduleRoute(current?.qualificationModuleID ?? "", query)}
            className="group flex min-h-28 flex-col justify-between rounded-md border border-l-4 border-border border-l-primary bg-surface p-3 shadow-sm transition-[border-color,box-shadow] hover:border-primary hover:shadow-md"
        >
            <span className="flex items-start justify-between gap-2">
                <span className="font-medium leading-snug text-text-strong group-hover:text-primary-text">{s.moduleName}</span>
                <Icon icon={["fad", "chevron-right"]} className="mt-1 shrink-0 text-xs text-text-weak group-hover:text-primary-text" />
            </span>
            <span className="mt-3 flex flex-col gap-1.5">
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-weak">
                    {current?.cycleName && (
                        <span title={`${terms.length} term${terms.length === 1 ? "" : "s"}; opens on the current one`}>
                            {current.cycleName} · current
                        </span>
                    )}
                    <span className="inline-flex items-center gap-1" title="Students in the current term">
                        <Icon icon={["fad", "users"]} /> {current?.studentCount ?? 0}
                    </span>
                </span>
                {mine.length > 0 && (
                    <span className="flex flex-wrap gap-1">
                        {mine.map((g) => (
                            <Badge key={g.id} tone="primary">
                                {g.name}
                            </Badge>
                        ))}
                    </span>
                )}
            </span>
        </Link>
    );
};

export const NoModule: React.FC = () => (
    <Card>
        <EmptyState icon={["fad", "graduation-cap"]} title="Subject not found" hint="It may have been removed, or belong to another organization." />
    </Card>
);

export default MarksScreen;
